<template>
  <multiselect-dropdown
    :value="selectedConsents"
    :options="consentOptions"
    :scrollable-parent="''"
    :placeholder="$t('COMMON.CONSENT_TEMPLATE', 2)"
    data-cy="consent-step-templates-dropdown"
    @change="handleSelectConsent"
  />
  <lf-dropdown
    :value="selectedOwnerOptionLabel"
    :options="options?.definition.owners_required"
    :placeholder="$t('CHECKOUT.NUMBER_OF_OWNERS')"
    full-width
    data-cy="consent-step-owners-dropdown"
    @change="handleSelectRequiredOwner"
  />
</template>

<script setup lang="ts">
import { computed, onMounted, ref, watch } from "vue";
import { useI18n } from "vue-i18n";

import { storeToRefs } from "pinia";

import { useCheckoutOptionsQuery } from "@/hooks/queries/options";
import { useCheckoutStore } from "@/stores/checkout";
import useConsentTemplateStore from "@/stores/consentTemplate";

import LfDropdown from "@/components/ui/inputs/LfDropdown.vue";
import MultiselectDropdown from "@/components/ui/inputs/MultiselectDropdown.vue";

import type { MultiSelectOption } from "@/models/options";

const { t } = useI18n();
const consentTemplateStore = useConsentTemplateStore();
const { data: options } = useCheckoutOptionsQuery();
const { activeTemplateClient, selectedStep } = storeToRefs(useCheckoutStore());

const selectedOwnerOption = ref(
  selectedStep.value?.templates?.[0]?.owners_required ?? "all"
);

const consentOptions = computed<MultiSelectOption[]>(() =>
  consentTemplateStore.templates.data
    .filter(
      (consent) =>
        consent.client_id === activeTemplateClient.value?.id ||
        consent.is_lendflow_template
    )
    .map((template) => ({
      id: template.id,
      name: template.name,
    }))
);

const selectedOwnerOptionLabel = computed(() => {
  if (selectedOwnerOption.value === "all") {
    return t("CHECKOUT.OWNER_OPTIONS.ALL_OWNERS");
  }

  if (selectedOwnerOption.value === "primary") {
    return t("CHECKOUT.OWNER_OPTIONS.PRIMARY_OWNER");
  }

  if (selectedOwnerOption.value === "any") {
    return t("CHECKOUT.OWNER_OPTIONS.ANY_OWNER");
  }

  return selectedOwnerOption.value;
});

const selectedConsents = computed(
  () =>
    selectedStep.value?.templates?.map((template) => ({
      id: template.id as string,
    })) ?? []
);

const handleSelectConsent = (value: Pick<MultiSelectOption, "id">[]) => {
  if (!selectedStep.value) {
    return;
  }

  if (!selectedStep.value.templates) {
    selectedStep.value.templates = [];
  }

  selectedStep.value.templates = value.map((value) => ({
    id: value.id,
    owners_required: selectedOwnerOption.value,
  }));
};

const handleSelectRequiredOwner = (value: string | number) => {
  selectedOwnerOption.value = value as string;
};

onMounted(consentTemplateStore.getTemplates);

watch(
  () => selectedOwnerOption.value,
  (newSelectedOwnerOption) => {
    selectedStep.value?.templates?.forEach((template) => {
      template.owners_required = newSelectedOwnerOption;
    });
  },
  { immediate: true }
);
</script>
